<template>
    <nav
        id="top_of_left_column"
        ref="leftCol"
        tabindex="-1"
        :aria-label="
            $formatMessage({ defaultMessage: 'Left (navigation) column', id: 'whatsUp.whatsUpView.leftColumn' })
        "
        class="vertical menu">
        <slot name="additional-left-col-content"></slot>

        <SkyCard
            ref="navCard"
            data-region="feed-link-list"
            data-test="left-column-nav"
            class="menu left-column-nav">
            <ul>
                <li
                    v-for="section in getSections()"
                    :key="section.name"
                    class="item main-feed-item">
                    <SkyButton
                        v-if="section.url"
                        :href="section.url"
                        data-test="section-link"
                        :aria-label="section.title"
                        variant="text"
                        v-bind="getLinkAttrs(section)"
                        :class="{ active: isLastActiveSection(section) }"
                        @click="onSectionClick(section)">
                        <template #iconLeft="{ attrs: iconAttrs }">
                            <SkyIcon>
                                <component
                                    :is="getIcon(section)"
                                    v-bind="iconAttrs"
                                    :active="isLastActiveSection(section)"
                                    :class="{ 'custom-nav-icon': getIcon(section) === 'hash-icon' }"></component>
                            </SkyIcon>
                        </template>
                        <SkyText
                            variant="strong"
                            tag="span">
                            {{ section.title }}
                        </SkyText>
                    </SkyButton>

                    <div
                        class="left-column-section"
                        data-test="left-column-section">
                        <div
                            v-if="section.feeds && section.feeds.length > 0"
                            class="header"
                            v-bind="getSectionAttrs(section)"
                            :class="{ active: isLastActiveSection(section) }">
                            <SkyText variant="strong">
                                <SkyIcon v-slot="{ attrs }">
                                    <Users v-bind="attrs"></Users>
                                </SkyIcon>

                                {{ section.title }}
                            </SkyText>
                        </div>

                        <ul
                            v-for="(feed, feedIndex) in section.feeds"
                            :key="feed.title">
                            <LeftColumnGroupLink
                                :index="feedIndex + 1"
                                :list-length="section.feeds.length"
                                :group="feed"
                                @click="clicked"></LeftColumnGroupLink>
                        </ul>
                    </div>
                </li>
            </ul>
        </SkyCard>
    </nav>
</template>

<script>
    import LeftColumnGroupLink from "@eg_ui/src/components/globals/_layouts/LeftColumnGroupLink/LeftColumnGroupLink.vue";
    import { accessibilityMenu } from "@eg_ui/src/mixins/globals/accessibilityMenu";
    import { getPendoAttribute } from "@eg_ui/src/utils/functions";
    import { GLOBAL_EVENTS, globalEventHandler } from "@eg_ui/src/utils/libs";
    import HouseHeart from "@fortawesome/fontawesome-pro/svgs/regular/house-heart.svg";
    import Users from "@fortawesome/fontawesome-pro/svgs/regular/users.svg";

    import { SkyButton, SkyCard, SkyIcon, SkyText } from "@skyline2/vue";

    export default {
        components: {
            HouseHeart,
            LeftColumnGroupLink,
            SkyButton,
            SkyCard,
            SkyIcon,
            SkyText,
            Users,
        },
        mixins: [accessibilityMenu],
        props: {
            active: { type: Boolean, default: true },
            config: { type: Object, default: () => {} },
            /* structure expected to be as follows:
            {
                "sections": [
                    {
                        "name": String,
                        "title": String,
                        "url": String,
                        "feeds": Array [
                            "title": String,
                            "url": String,
                            "uuid": String
                        ]
                    }
                ]
            } */
        },
        emits: ["clicked"],
        computed: {
            lastActiveSection() {
                // Iterate through sections in reverse order to find the last active section
                for (let i = this.config.sections.length - 1; i >= 0; i--) {
                    const section = this.config.sections[i];
                    if (this.isActiveSection(section)) {
                        return section;
                    }
                }
                return null;
            },
        },
        mounted() {
            this.$nextTick(() => {
                this.$nextTick(() => {
                    // We need to manually strip the role here because SkyCard will include it otherwise, even
                    // if we set role="" on it. We do not need the role in this particular instance because
                    // any aria-label would just duplicate the label that already exists on the column.
                    if (this.$refs.navCard) {
                        this.$refs.navCard.$el.removeAttribute("role");
                    }
                });
            });
        },
        methods: {
            clicked() {
                this.$emit("clicked");
            },
            getIcon(section) {
                const icons = {
                    feed: "house-heart",
                };

                return icons[section.name] ? icons[section.name] : "";
            },
            getLinkAttrs(section) {
                const attrs = this.getSectionAttrs(section);

                if (this.isLastActiveSection(section)) {
                    attrs["aria-current"] = "page";
                }

                return attrs;
            },
            getSectionAttrs(section) {
                let attrs;

                if (section.pendoId) {
                    attrs = { ...getPendoAttribute(section.pendoId) };
                } else {
                    attrs = {};
                }

                return attrs;
            },
            getSections() {
                // Return the sections from the config if they don't have mobile_only set to true
                return this.config.sections.filter((section) => {
                    return !section.mobile_only;
                });
            },
            isActiveSection(section) {
                // Special case for "My Feed" section with URL "/"
                if (this.isRootPathActive(section)) {
                    return true;
                }

                // Check if the current URL starts with the section's URL (excluding root path)
                if (section.url !== "/" && this.$route.path.startsWith(section.url)) {
                    return true;
                }

                // Check if the current URL matches any of the additional URLs
                if (this.isAdditionalUrlActive(section)) {
                    return true;
                }

                // Check if any of the feeds' URLs are included in the current URL
                if (section.feeds) {
                    for (const feed of section.feeds) {
                        if (feed.url !== "/" && this.$route.path.includes(feed.url)) {
                            return true;
                        }
                    }
                }

                return false;
            },
            isAdditionalUrlActive(section) {
                if (section.additional_urls) {
                    for (const url of section.additional_urls) {
                        if (url !== "/" && this.$route.path === url) {
                            return true;
                        }
                    }
                }
                return false;
            },
            isLastActiveSection(section) {
                return this.lastActiveSection === section;
            },
            isRootPathActive(section) {
                return section.url === "/" && this.$route.path === "/" && this.active;
            },
            onSectionClick(section) {
                globalEventHandler.emit(GLOBAL_EVENTS.ANALYTICS.EVENT, {
                    event: "main_nav",
                    sub_type: section.title,
                });
                this.clicked();
            },
        },
    };
</script>

<style lang="scss">
    @use "./LeftColumnNav.scss";
</style>
